import { toInputDate } from "@/utils/dateUtils";
import {
  VehicleCardDetails,
  BaseVehicleCard,
  VehicleCardStatus,
  CreateVehicleCardRequest,
  CreateVehicleCardManuallyRequest,
  CreateVehicleCardManuallyResponse,
  GetVehicleCardDetailsResponse,
  GetAllVehicleCardsResponse,
  VehicleCardImages,
  ServiceDetail,
  VehicleCardExteriorImage,
  VehicleCardInteriorImage,
  VehicleCardTireImage,
} from "@/types/vehicleCard";
import { VehicleDetails } from "@/types/vehicleDetails";
import { UserManagementService } from "../user/userManagementService";
import { FileObject } from "@/types/shared/file";
import { api } from "@/integrations/clients/carAuction/carAuctionClient";

/**
 * Every column the vehicle-card car-information PATCH accepts, mirroring
 * `VehicleCardCarInformation` in schema.prisma plus `vin`, which the repository
 * peels off onto `VehicleCard`. The backend spreads the request body straight
 * into Prisma, so a key outside this list throws and a key missing from it is
 * silently discarded. Add new columns here rather than at a call site.
 */
const VEHICLE_CARD_CAR_INFO_FIELDS = [
  "make",
  "model",
  "variant",
  "engine",
  "trim",
  "year",
  "fuelType",
  "bodyType",
  "color",
  "transmission",
  "equipment",
  "mileage",
  "condition",
  "vehicleType",
  "horsepower",
  "keys",
  "currentTireType",
  "currentRimType",
  "extraTireSet",
  "extraTireType",
  "extraRimType",
  "registrationDate",
  "electricDriveRange",
  "engineSize",
  "vin",
] as const;

const toIntOrNull = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = typeof value === "number" ? value : parseInt(String(value), 10);
  return Number.isNaN(parsed) ? null : Math.trunc(parsed);
};

const toFloatOrNull = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") return null;
  const parsed =
    typeof value === "number"
      ? value
      : parseFloat(String(value).replace(",", "."));
  return Number.isNaN(parsed) ? null : parsed;
};

// Mock storage for vehicle card documents (in a real app, this would be handled by backend)
// TODO: Remove when integrated fully
const mockVehicleCardDocuments: {
  [vehicleCardId: string]: {
    owner_change: FileObject[];
    other_documents: FileObject[];
    invoice_file?: FileObject;
    contract_file?: FileObject;
  };
} = {};

// Helper function to get documents for a vehicle card
// TODO: Remove when integrated fully
const getVehicleCardDocuments = (vehicleCardId: string) => {
  if (!mockVehicleCardDocuments[vehicleCardId]) {
    mockVehicleCardDocuments[vehicleCardId] = {
      owner_change: [],
      other_documents: [],
    };
  }
  return mockVehicleCardDocuments[vehicleCardId];
};

// Mock storage for vehicle card images (in a real app, this would be handled by backend)
// TODO: Remove when integrated fully
const mockVehicleCardImages: {
  [vehicleCardId: string]: VehicleCardImages;
} = {};

// Helper function to get images for a vehicle card
// TODO: Remove when integrated fully
const getVehicleCardImages = (
  vehicleCardId: string,
): VehicleCardImages | null => {
  return mockVehicleCardImages[vehicleCardId] || null;
};

// Mock storage for vehicle card service details (in a real app, this would be handled by backend)
// TODO: Remove when integrated fully
const mockVehicleCardServiceDetails: {
  [vehicleCardId: string]: {
    serviceDetails: ServiceDetail[];
    damageComments: { [imageId: string]: string };
  };
} = {};

// Helper function to get service details for a vehicle card
// TODO: Remove when integrated fully
const getVehicleCardServiceDetails = (vehicleCardId: string) => {
  if (!mockVehicleCardServiceDetails[vehicleCardId]) {
    mockVehicleCardServiceDetails[vehicleCardId] = {
      serviceDetails: [],
      damageComments: {},
    };
  }
  return mockVehicleCardServiceDetails[vehicleCardId];
};

export class VehicleCardService {
  /*
TODO: A couple of things seems missing? 
          - update?
*/

  // Get all vehicle cards with pagination and optional query parameters
  static async getAllVehicleCards(
    page: number = 1,
    limit: number = 10,
    query?: { search?: string; status?: string },
  ): Promise<GetAllVehicleCardsResponse> {
    // Build query parameters
    const params = new URLSearchParams();
    params.append("page", String(page));
    params.append("limit", String(limit));

    // Add search parameter if provided
    if (query?.search && query.search.trim()) {
      params.append("search", query.search.trim());
    }

    // Backend expects uppercase status (ACTIVE, INACTIVE, etc.) but frontend uses lowercase
    // Convert lowercase status to uppercase for backend
    if (query?.status && query.status !== "all") {
      const statusMap: { [key: string]: string } = {
        active: "ACTIVE",
        inactive: "INACTIVE",
        completed: "COMPLETED",
        incomplete: "INCOMPLETE",
      };
      const backendStatus =
        statusMap[query.status.toLowerCase()] || query.status.toUpperCase();
      params.append("status", backendStatus);
    }

    const response = await api.get<GetAllVehicleCardsResponse>(
      `/admin/vehicle-cards?${params.toString()}`,
    );

    // Calculate totalPages from total and limit
    const totalPages = Math.ceil(response.total / limit);

    return {
      success: response.success,
      total: response.total,
      data: response.data,
      pagination: {
        page,
        limit,
        total: response.total,
        totalPages,
        ...(response.pagination && {
          next: response.pagination.next,
          prev: response.pagination.prev,
        }),
      },
    };
  }

  // Get vehicle card with full details
  static async getVehicleCard(
    vehicleCardId: string,
  ): Promise<GetVehicleCardDetailsResponse> {
    // Call backend API to get vehicle card
    const response = await api.get<GetVehicleCardDetailsResponse>(
      `/admin/vehicle-cards/${vehicleCardId}`,
    );
    return response;
  }

  // Create vehicle card from lead
  static async createVehicleCard(
    request: CreateVehicleCardRequest,
  ): Promise<{ success: boolean; vehicleCardId: string; id: string }> {
    const response = await api.post<{
      success: boolean;
      data: VehicleCardDetails;
    }>("/admin/vehicle-cards/from-lead", {
      leadId: request.leadId,
      ownerId: request.ownerId, // Optional: User ID for guest leads
    });

    return {
      success: response.success,
      vehicleCardId: response.data.vehicleCardId,
      id: response.data.id,
    };
  }

  // Create vehicle card manually (admin)
  static async createVehicleCardManually(
    request: CreateVehicleCardManuallyRequest,
  ): Promise<CreateVehicleCardManuallyResponse> {
    const response = await api.post<CreateVehicleCardManuallyResponse>(
      "/admin/vehicle-cards/manually",
      request,
    );
    return response;
  }

  // Add comment to vehicle card
  static async addComment(
    vehicleCardId: string,
    comment: { text: string; author: string; mentions?: string[] },
  ): Promise<{ success: boolean; commentId?: string }> {
    const response = await api.post<{
      success: boolean;
      data: { id: string; text: string; createdAt: Date };
    }>(`/admin/vehicle-cards/${vehicleCardId}/comments`, {
      text: comment.text,
      mentions: comment.mentions || [],
    });
    return { success: response.success, commentId: response.data?.id };
  }

  static async updateComment(
    vehicleCardId: string,
    commentId: string,
    text: string,
  ): Promise<void> {
    await api.put(
      `/admin/vehicle-cards/${vehicleCardId}/comments/${commentId}`,
      { text },
    );
  }

  static async deleteComment(
    vehicleCardId: string,
    commentId: string,
  ): Promise<void> {
    await api.delete(
      `/admin/vehicle-cards/${vehicleCardId}/comments/${commentId}`,
    );
  }

  // Update vehicle card vehicle details
  static async updateVehicleCardVehicleDetails(
    vehicleCardId: string,
    vehicleDetails: VehicleDetails,
  ): Promise<{ success: boolean }> {
    // Structural build over the shared column list - see
    // VEHICLE_CARD_CAR_INFO_FIELDS. This used to be a hand-enumerated object
    // literal that simply had no `engineSize` line, so every Motorstorlek the
    // admin typed was dropped on the floor before the request was even sent.
    const source = vehicleDetails as unknown as Record<string, unknown>;
    const carInfoRequest: Record<string, unknown> = {};
    for (const field of VEHICLE_CARD_CAR_INFO_FIELDS) {
      if (source[field] !== undefined) carInfoRequest[field] = source[field];
    }

    // Numeric columns: the editor holds these as strings, and the backend
    // spreads the patch straight into Prisma, which rejects a string for an
    // Int/Float column.
    carInfoRequest.year = toIntOrNull(source.year);
    carInfoRequest.mileage = toIntOrNull(source.mileage);
    carInfoRequest.horsepower = toIntOrNull(source.horsepower);
    if (source.engineSize !== undefined) {
      carInfoRequest.engineSize = toFloatOrNull(source.engineSize);
    }
    // DateTime column - Prisma needs a full ISO timestamp, not "YYYY-MM-DD".
    if (source.registrationDate !== undefined) {
      const parsed = new Date(String(source.registrationDate));
      carInfoRequest.registrationDate = isNaN(parsed.getTime())
        ? null
        : parsed.toISOString();
    }

    const response = await api.patch<{
      success: boolean;
      data: VehicleCardDetails;
    }>(`/admin/vehicle-cards/${vehicleCardId}/car-information`, carInfoRequest);
    return { success: response.success };
  }

  // Update vehicle card status
  // Backend handles conversion from lowercase to enum automatically
  static async updateVehicleCardStatus(
    vehicleCardId: string,
    status: string,
  ): Promise<{ success: boolean }> {
    const response = await api.patch<{ success: boolean }>(
      `/admin/vehicle-cards/${vehicleCardId}/status`,
      { status },
    );
    return response;
  }

  // Update vehicle card owner
  static async updateVehicleCardOwner(
    vehicleCardId: string,
    customerId: string,
  ): Promise<{ success: boolean }> {
    const response = await api.patch<{ success: boolean }>(
      `/admin/vehicle-cards/${vehicleCardId}/owner`,
      { customerId },
    );
    return response;
  }

  // TODO: Dessa om dokument + bilder ej hanterade ännu
  // Add vehicle card document
  static async addVehicleCardDocument(
    vehicleCardId: string,
    file: File,
    category: "invoice" | "contract" | "ownerChange" | "other",
  ): Promise<FileObject> {
    // Validate file type and size using constants
    const { SUPPORTED_FILE_TYPES, MAX_FILE_SIZE_BYTES, MAX_FILE_SIZE_MB } =
      await import("@/config/constants");
    if (!SUPPORTED_FILE_TYPES.includes(file.type)) {
      const allowedExtensions = SUPPORTED_FILE_TYPES.map((t) => {
        const ext =
          t.split("/")[1]?.toUpperCase() ||
          t.split(".")[1]?.toUpperCase() ||
          "UNKNOWN";
        return ext === "JPEG" || ext === "JPG" ? "JPEG" : ext;
      }).filter((v, i, a) => a.indexOf(v) === i); // Remove duplicates
      throw new Error(
        `Ogiltigt filformat. Endast ${allowedExtensions.join(", ")} är tillåtna.`,
      );
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      throw new Error(
        `Filen är för stor. Max storlek är ${MAX_FILE_SIZE_MB}MB.`,
      );
    }

    // Only handle ownerChange and other documents here (contract/invoice use their own endpoints)
    if (category !== "ownerChange" && category !== "other") {
      throw new Error(`Category ${category} should use dedicated endpoint`);
    }

    const { MediaService } = await import("@/services/media/mediaService");

    // Map category to document subPath for S3
    const documentSubPath =
      category === "ownerChange" ? "owner-change" : "other";

    // Step 1: Request presigned URL with vehicleCardId and document category
    const { uploadUrl, s3Key } = await MediaService.requestPresignedUploadUrl(
      "vehicleCard",
      vehicleCardId,
      {
        originalName: file.name,
        mimeType: file.type,
        sizeBytes: file.size,
      },
      documentSubPath, // subPath for document category
    );

    // Step 2: Upload file to S3
    await MediaService.uploadFileToS3(uploadUrl, file);

    // Step 3: Create VehicleCardDocument record first (via backend endpoint)
    const backendCategory =
      category === "ownerChange" ? "CHANGE_OF_OWNERSHIP" : "OTHER";

    const vehicleCardDocumentResponse = await api.post<{
      success: boolean;
      data: { id: string };
    }>(`/admin/vehicle-cards/${vehicleCardId}/documents`, {
      category: backendCategory,
    });

    // API client unwraps response.data, so vehicleCardDocumentResponse is already { success: true, data: { id: string } }
    if (!vehicleCardDocumentResponse.success) {
      throw new Error(
        "Failed to create vehicle card document: Server returned unsuccessful response",
      );
    }

    if (!vehicleCardDocumentResponse.data) {
      throw new Error(
        "Failed to create vehicle card document: Server response missing data",
      );
    }

    if (!vehicleCardDocumentResponse.data.id) {
      throw new Error(
        "Failed to create vehicle card document: Server response missing document ID",
      );
    }

    const vehicleCardDocumentId = vehicleCardDocumentResponse.data.id;

    // Step 4: Create Media record with vehicleCardDocumentId
    const mediaResponse = await MediaService.createMediaAfterUpload(
      s3Key,
      {
        originalName: file.name,
        mimeType: file.type,
        sizeBytes: file.size,
      },
      { vehicleCardDocumentId },
    );

    // Step 5: Update Media status to UPLOADED
    await MediaService.updateMediaStatus(mediaResponse.id, "UPLOADED");

    // Step 6: Fetch presigned download URL for the document
    const downloadUrl = await MediaService.getDownloadUrl(
      mediaResponse.id,
      3600,
    );

    // Return FileObject with presigned URL
    return {
      id: vehicleCardDocumentId, // VehicleCardDocument ID
      url: downloadUrl,
      name: file.name,
      type: file.type,
      size: file.size,
      mediaId: mediaResponse.id, // Keep mediaId for reference
    };
  }

  // Remove vehicle card document
  static async removeVehicleCardDocument(
    vehicleCardId: string,
    documentId: string,
  ): Promise<{ success: boolean }> {
    const response = await api.delete<{ success: boolean }>(
      `/admin/vehicle-cards/${vehicleCardId}/documents/${documentId}`,
    );
    return response;
  }

  // Add vehicle card image
  static async addVehicleCardImage(
    vehicleCardId: string,
    category: "exterior" | "interior" | "damage" | "service" | "tires",
    file: File,
    isThumbnail?: boolean,
    tireType?: "primary" | "secondary",
  ): Promise<FileObject> {
    // Validate file type and size using constants
    const { SUPPORTED_IMAGE_TYPES, MAX_FILE_SIZE_BYTES, MAX_FILE_SIZE_MB } =
      await import("@/config/constants");
    if (!SUPPORTED_IMAGE_TYPES.includes(file.type)) {
      const allowedExtensions = SUPPORTED_IMAGE_TYPES.map((t) => {
        const ext = t.split("/")[1].toUpperCase();
        return ext === "JPEG" || ext === "JPG" ? "JPEG" : ext;
      }).filter((v, i, a) => a.indexOf(v) === i); // Remove duplicates
      throw new Error(
        `Ogiltigt filformat. Endast ${allowedExtensions.join(", ")} är tillåtna.`,
      );
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      throw new Error(
        `Filen är för stor. Max storlek är ${MAX_FILE_SIZE_MB}MB.`,
      );
    }

    // Compress image before upload - target max 0.7MB (700KB)
    const { compressImage } = await import("@/utils/imageCompression");
    let compressedFile: File;
    try {
      compressedFile = await compressImage(file, {
        maxSizeMB: 0.7, // Target maximum file size (700KB)
      });
    } catch (error) {
      compressedFile = file;
    }

    // Map frontend category to backend enum value
    const categoryMap: Record<typeof category, string> = {
      exterior: "EXTERIOR",
      interior: "INTERIOR",
      damage: "DAMAGES",
      service: "SERVICE",
      tires: "WHEELS",
    };
    const backendCategory = categoryMap[category];

    const { MediaService } = await import("@/services/media/mediaService");

    // Step 1: Request presigned URL with vehicleCardId and category
    // Use lowercase category for S3 path (exterior, interior, etc.)
    // Use compressed file metadata for accurate size
    const { uploadUrl, s3Key } = await MediaService.requestPresignedUploadUrl(
      "vehicleCard",
      vehicleCardId,
      {
        originalName: file.name, // Keep original filename
        mimeType: compressedFile.type, // Use compressed file type
        sizeBytes: compressedFile.size, // Use compressed file size
      },
      category, // subPath for category (exterior, interior, damage, service, tires) - lowercase for S3 path
    );

    // Step 2: Upload compressed file to S3
    await MediaService.uploadFileToS3(uploadUrl, compressedFile);

    // Step 3: Create VehicleCardImage record first (via backend endpoint)
    // We need to create the join table record first, then create Media with vehicleCardImageId
    // Use backend enum value (uppercase) for the category
    // Explicitly convert isThumbnail to boolean
    const isThumbnailBool = isThumbnail === true ? true : false;
    const vehicleCardImageResponse = await api.post<{
      success: boolean;
      data: { id: string };
    }>(`/admin/vehicle-cards/${vehicleCardId}/images`, {
      category: backendCategory, // Backend expects uppercase enum value
      isThumbnail: isThumbnailBool, // Explicitly convert to boolean
      tireType,
    });

    const vehicleCardImageId = vehicleCardImageResponse.data.id;

    // Step 4: Create Media record with vehicleCardImageId
    // Use compressed file metadata
    const mediaResponse = await MediaService.createMediaAfterUpload(
      s3Key,
      {
        originalName: file.name, // Keep original filename
        mimeType: compressedFile.type, // Use compressed file type
        sizeBytes: compressedFile.size, // Use compressed file size
      },
      { vehicleCardImageId },
    );

    // Step 5: Update Media status to UPLOADED
    await MediaService.updateMediaStatus(mediaResponse.id, "UPLOADED");

    // Step 6: Fetch presigned download URL for the image
    const downloadUrl = await MediaService.getDownloadUrl(
      mediaResponse.id,
      3600,
    );

    // Return FileObject with presigned URL
    // Use vehicleCardImageId as the id (not mediaResponse.id) to match backend response structure
    // Use compressed file metadata for size/type, but keep original filename
    // Include isThumbnail for exterior images
    const result: FileObject & { isThumbnail?: boolean } = {
      id: vehicleCardImageId, // VehicleCardImage ID (matches backend response)
      url: downloadUrl,
      name: file.name, // Keep original filename
      type: compressedFile.type, // Use compressed file type
      size: compressedFile.size, // Use compressed file size
      mediaId: mediaResponse.id, // Keep mediaId for reference
    };

    // Add isThumbnail for exterior images
    if (category === "exterior" && isThumbnailBool) {
      (result as any).isThumbnail = true;
    }

    return result;
  }

  // Remove vehicle card image
  static async removeVehicleCardImage(
    vehicleCardId: string,
    imageId: string,
  ): Promise<{ success: boolean }> {
    const response = await api.delete<{ success: boolean }>(
      `/admin/vehicle-cards/${vehicleCardId}/images/${imageId}`,
    );
    return response;
  }

  // Add vehicle card service detail
  static async addVehicleCardServiceDetail(
    vehicleCardId: string,
    serviceDetail: ServiceDetail,
  ): Promise<ServiceDetail> {
    try {
      const response = await api.post<{ success: boolean; data: any }>(
        `/admin/vehicle-cards/${vehicleCardId}/service`,
        {
          serviceDate:
            serviceDetail.date instanceof Date
              ? toInputDate(serviceDetail.date)
              : serviceDetail.date,
          mileage: serviceDetail.mileage,
          serviceType: serviceDetail.serviceType,
          notes: serviceDetail.notes || undefined,
        },
      );

      // API client unwraps response.data, so response is already { success: true, data: result }
      if (!response.success) {
        throw new Error(
          "Failed to create service detail: Server returned unsuccessful response",
        );
      }

      if (!response.data) {
        throw new Error(
          "Failed to create service detail: Server response missing data",
        );
      }

      const data = response.data;

      // Map backend response to frontend ServiceDetail format
      // Backend returns serviceDate as ISO string (serialized from Date)
      let serviceDate: Date;
      if (data.serviceDate instanceof Date) {
        serviceDate = data.serviceDate;
      } else if (typeof data.serviceDate === "string") {
        serviceDate = new Date(data.serviceDate);
        if (isNaN(serviceDate.getTime())) {
          serviceDate = new Date(); // Fallback
        }
      } else {
        serviceDate = new Date(); // Fallback
      }

      return {
        id: data.id,
        date: serviceDate,
        mileage: data.mileage,
        serviceType: data.serviceType,
        notes: data.notes || "",
      };
    } catch (error: any) {
      if (error.response?.data) {
      }
      throw error;
    }
  }

  // Remove vehicle card service detail
  static async removeVehicleCardServiceDetail(
    vehicleCardId: string,
    serviceDetailId: string,
  ): Promise<{ success: boolean }> {
    const response = await api.delete<{ success: boolean }>(
      `/admin/vehicle-cards/${vehicleCardId}/service/${serviceDetailId}`,
    );
    return response;
  }

  // Update vehicle card damage image comment
  static async updateVehicleCardDamageImageComment(
    vehicleCardId: string,
    imageId: string,
    comment: string,
  ): Promise<{ success: boolean }> {
    const response = await api.patch<{ success: boolean }>(
      `/admin/vehicle-cards/${vehicleCardId}/images/${imageId}/comment`,
      { comment },
    );
    return response;
  }
}
